import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import classnames from 'classnames';
import { RouterPaths } from '../Router/Router.constants';
import {
  tStep,
  tStepStatus,
  tSyncPhase,
  tSyncProgressToastProps,
} from './SyncProgressToast.types';

const AUTO_DISMISS_MS = 8000;

type tGetStepsOptions = {
  phase: tSyncPhase;
  profiled: number;
  total: number;
};

const getSteps = (options: tGetStepsOptions): tStep[] => {
  const { phase, profiled, total } = options;
  const statusForStep = (stepPhase: tSyncPhase): tStepStatus => {
    const order: tSyncPhase[] = ['contacts', 'history', 'profiling'];
    const currentIdx = order.indexOf(phase);
    const stepIdx = order.indexOf(stepPhase);

    if (phase === 'completed') return 'completed';
    if (stepIdx < currentIdx) return 'completed';
    if (stepIdx === currentIdx) return 'active';
    return 'pending';
  };

  return [
    {
      label: 'Sincronizando tus contactos',
      detail: '',
      status: statusForStep('contacts'),
    },
    {
      label: 'Descargando historial de mensajes',
      detail: '',
      status: statusForStep('history'),
    },
    {
      label: 'Organizando tus conversaciones con IA',
      detail:
        statusForStep('profiling') === 'active' && total > 0
          ? `${profiled} de ${total} conversaciones analizadas`
          : statusForStep('profiling') === 'completed'
            ? ''
            : '',
      status: statusForStep('profiling'),
    },
  ];
};

const StepIcon = ({ status }: { status: tStepStatus }) => {
  if (status === 'completed') {
    return (
      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-success text-white">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-3 w-3"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      </div>
    );
  }

  if (status === 'active') {
    return (
      <div className="flex h-5 w-5 items-center justify-center">
        <span className="loading loading-spinner loading-xs text-primary" />
      </div>
    );
  }

  return (
    <div className="h-5 w-5 rounded-full border-2 border-base-300" />
  );
};

export default function SyncProgressToast(props: tSyncProgressToastProps) {
  const { phase, profiled, total, onDismiss } = props;
  const navigate = useNavigate();
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const steps = getSteps({ phase, profiled, total });
  const isCompleted = phase === 'completed';
  const progress = total > 0 ? Math.round((profiled / total) * 100) : 0;

  useEffect(() => {
    if (isCompleted) {
      dismissTimerRef.current = setTimeout(onDismiss, AUTO_DISMISS_MS);
    }

    return () => {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current);
      }
    };
  }, [isCompleted, onDismiss]);

  const handleViewFunnel = () => {
    navigate(RouterPaths.funnels);
    onDismiss();
  };

  return (
    <AnimatePresence>
      {phase !== 'idle' && (
        <motion.div
          initial={{ x: 400, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 400, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="fixed right-6 top-20 z-50 w-80 rounded-xl border border-base-200 bg-base-100 p-4 shadow-lg"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm font-semibold">
              {isCompleted
                ? 'Listo, tus conversaciones están organizadas'
                : 'Configurando tu espacio de trabajo'}
            </span>
            <button
              onClick={onDismiss}
              className="btn btn-circle btn-ghost btn-xs"
              aria-label="Cerrar"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="flex flex-col gap-2">
            {steps.map((step) => (
              <div key={step.label} className="flex items-start gap-2">
                <div className="mt-0.5 flex-shrink-0">
                  <StepIcon status={step.status} />
                </div>
                <div className="flex-1">
                  <span
                    className={classnames('text-xs', {
                      'text-base-content': step.status !== 'pending',
                      'text-base-content/40': step.status === 'pending',
                    })}
                  >
                    {step.label}
                  </span>
                  {step.status === 'active' && step.detail && (
                    <div className="mt-1">
                      <div
                        className="h-1.5 w-full overflow-hidden rounded-full bg-base-200"
                        role="progressbar"
                        aria-valuenow={progress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuetext={step.detail}
                      >
                        <div
                          className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <span className="mt-0.5 block text-xs text-base-content/60">
                        {step.detail}
                      </span>
                    </div>
                  )}
                  {step.status === 'completed' && step.detail && (
                    <span className="text-xs text-success">{step.detail}</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {isCompleted && (
            <div className="mt-3 flex items-center justify-between">
              <button
                onClick={handleViewFunnel}
                className="btn btn-link btn-sm p-0 text-primary no-underline"
              >
                Ver embudo →
              </button>
              <div className="h-0.5 flex-1 ml-2 overflow-hidden rounded-full bg-base-200">
                <motion.div
                  className="h-full bg-base-300"
                  initial={{ width: '100%' }}
                  animate={{ width: '0%' }}
                  transition={{ duration: AUTO_DISMISS_MS / 1000, ease: 'linear' }}
                />
              </div>
            </div>
          )}

          {!isCompleted && (
            <p className="mt-3 text-xs text-base-content/50">
              Puedes seguir usando la plataforma
            </p>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
